/* Phase 6A-4 — F3 Tour Button + F4 Auto-Launcher.
 *
 * Two pieces exported from one file:
 *   1. <TourButton/>   — persistent header icon with a dropdown.
 *      CEO sees a submenu to launch ANY role's tour (so the executive can
 *      preview new-joiner experience).
 *   2. <TourAutoLauncher/> — invisible component placed in AppShell that
 *      polls GET /api/me/tour/status on mount and fires the user's role
 *      tour automatically on first login (unless tour_skipped=true).
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Compass, ChevronDown, PlayCircle } from "lucide-react";
import { useAuth } from "@/store/auth";
import { useMatrix } from "@/hooks/usePermission";
import { runTour, hasCompletedLocally, isSkippedLocal } from "@/lib/tours/tourEngine";
import { ROLE_LABELS, scripts as TOUR_SCRIPTS } from "@/lib/tours/scripts";
import api from "@/lib/api";

/* --------------------- Tour Button --------------------- */
export function TourButton() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // D54-f Group F6 -- CEO/ADMIN role check migrated to layered matrix.
  const canPreviewOther = useMatrix("platform_admin_access");
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }
  }, [open]);

  const myRole = String(user?.role || "SALES").toUpperCase();
  const startMine = () => {
    setOpen(false);
    runTour(myRole, { navigate: nav, user });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-tour-id="tour-button"
        data-testid="tour-button"
        title={t("tour.startTooltip", "Take the tour")}
        onClick={() => {
          if (canPreviewOther) {
            setOpen((o) => !o);
          } else {
            startMine();
          }
        }}
        className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl bg-white/5 border border-[var(--surface-border)] hover:bg-white/10 text-xs uppercase tracking-widest"
      >
        <Compass className="h-4 w-4 text-sky-300" />
        <span className="hidden md:inline">{t("tour.cta", "Tour")}</span>
        {canPreviewOther && (
          <ChevronDown className="h-3 w-3 opacity-60" />
        )}
      </button>

      {open && (
        <div
          data-testid="tour-button-menu"
          className="absolute right-0 mt-2 w-64 rounded-xl border border-[var(--surface-border)] bg-card/95 backdrop-blur-xl shadow-2xl p-2 z-50"
        >
          <button
            type="button"
            onClick={startMine}
            data-testid="tour-start-mine"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 text-left"
          >
            <PlayCircle className="h-4 w-4 text-sky-300" />
            <div className="flex-1">
              <div className="text-sm font-medium">
                {t("tour.startMine", "Start my tour")}
              </div>
              <div className="text-[10px] text-foreground/55 uppercase tracking-widest">
                {myRole}
              </div>
            </div>
          </button>
          <div className="my-1 border-t border-white/5" />
          <div className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-widest text-foreground/45">
            {t("tour.previewOther", "Preview another role")}
          </div>
          {Object.keys(TOUR_SCRIPTS).map((roleKey) => (
            <button
              type="button"
              key={roleKey}
              onClick={() => {
                setOpen(false);
                runTour(roleKey, { navigate: nav, user });
              }}
              data-testid={`tour-preview-${roleKey.toLowerCase()}`}
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg hover:bg-white/5 text-left text-xs"
            >
              <span className="text-foreground/80">{ROLE_LABELS[roleKey] || roleKey}</span>
              <span className="text-[10px] text-foreground/40">
                {TOUR_SCRIPTS[roleKey]?.steps?.length ?? 0} steps
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* --------------------- Auto-Launcher --------------------- */
export function TourAutoLauncher() {
  const { user } = useAuth();
  const nav = useNavigate();
  const fired = useRef(false);

  useEffect(() => {
    if (!user?.id || fired.current) return;
    // D54-f Group G -- renamed local var `role` -> `roleCode` for
    // clarity (tour content is keyed by legacy role code strings).
    // Once the `role` field is removed we'll switch to designation
    // -> role_code derivation. Keeping backward-compat for now.
    const roleCode = String(user.role || "").toUpperCase();
    if (!roleCode) return;

    // Dispatch 13.5 -- do NOT hijack deep-links. If the user lands on
    // something other than "/" (e.g. /profile, /tasks, /notes) we must
    // not force-navigate them to the tour's first step. The tour is
    // an onboarding aid, not a router override. Users who want it can
    // still start it manually from the top-bar TourButton.
    if (typeof window !== "undefined" && window.location.pathname !== "/") {
      return;
    }
    fired.current = true;

    // Fast local short-circuits BEFORE any network call.
    if (isSkippedLocal()) return;
    if (hasCompletedLocally(roleCode)) return;

    api
      .get("/me/tour/status")
      .then(({ data }) => {
        if (data?.tour_skipped) return;
        const done = data?.tour_completed || {};
        if (done[roleCode]) return;
        // Defer one paint so the dashboard is ready, and re-check the
        // path one more time: the user may have navigated away between
        // login and this callback (e.g. clicked "My Profile" in the
        // topbar the moment the shell mounted).
        setTimeout(() => {
          if (typeof window !== "undefined" && window.location.pathname !== "/") {
            return;
          }
          runTour(roleCode, { navigate: nav, user });
        }, 1200);
      })
      .catch(() => {
        /* server-flag fetch failed — fall back to localStorage-only behaviour */
      });
  }, [user?.id, user?.role, nav]);

  return null;
}

export default TourButton;
